import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFirebaseToken } from "../utils/getFirebaseToken";
import { fetchPurchaseOrders } from "../services/erpPurchaseOrders";
import { fetchPartners } from "../services/erpMasterData";

function formatDateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export default function PurchaseOrdersPage() {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getFirebaseToken();

      const [poRes, partnersRes] = await Promise.all([
        fetchPurchaseOrders(token, {
          q: q || undefined,
          partner_id: partnerFilter || undefined,
          status: statusFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }),
        fetchPartners(token),
      ]);

      setRows(poRes.rows || []);
      setPartners(partnersRes.rows || []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load purchase orders."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Purchase Orders</h1>
          <p className="text-slate-600">
            Manage procurement orders by supplier.
          </p>
        </div>

        <Link
          to="/erp/purchase-orders/new"
          className="px-4 py-2 rounded-lg bg-black text-white"
        >
          New Purchase Order
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="grid gap-3 md:grid-cols-5">
          <input
            type="text"
            placeholder="Search PO number or supplier..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="border rounded-lg px-3 py-2"
          />

          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All suppliers</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} - ` : ""}
                {p.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All statuses</option>
            <option value="DRAFT">DRAFT</option>
            <option value="APPROVED">APPROVED</option>
            <option value="ORDERED">ORDERED</option>
            <option value="PARTIAL_RECEIVED">PARTIAL_RECEIVED</option>
            <option value="RECEIVED">RECEIVED</option>
            <option value="CLOSED">CLOSED</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded-lg bg-black text-white"
          >
            Search
          </button>

          <button
            type="button"
            onClick={() => {
              setQ("");
              setPartnerFilter("");
              setStatusFilter("");
              setDateFrom("");
              setDateTo("");
            }}
            className="px-4 py-2 rounded-lg border"
          >
            Clear
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-sm border overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-3 py-3 text-left">PO Number</th>
              <th className="px-3 py-3 text-left">Supplier</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Order Date</th>
              <th className="px-3 py-3 text-left">Expected Date</th>
              <th className="px-3 py-3 text-right">Total</th>
              <th className="px-3 py-3 text-left">Currency</th>
              <th className="px-3 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                  No purchase orders found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-3 font-medium">{row.po_number}</td>
                  <td className="px-3 py-3">
                    {row.partner_code ? `${row.partner_code} - ` : ""}
                    {row.partner_name}
                  </td>
                  <td className="px-3 py-3">{row.status}</td>
                  <td className="px-3 py-3">{formatDateOnly(row.order_date)}</td>
                  <td className="px-3 py-3">{formatDateOnly(row.expected_date)}</td>
                  <td className="px-3 py-3 text-right">
                    {Number(row.total_amount || 0).toFixed(4)}
                  </td>
                  <td className="px-3 py-3">{row.currency}</td>
                  <td className="px-3 py-3">
                    <Link
                      to={`/erp/purchase-orders/${row.id}`}
                      className="px-3 py-1.5 rounded-lg border"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}